import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaRetweet, FaChartLine, FaInfoCircle, FaCheckCircle, FaQuestionCircle } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const RetirementCalculator = () => {
  const [formData, setFormData] = useState({
    currentAge: '',
    retirementAge: '',
    currentSavings: '',
    monthlySavings: '',
    expectedIncome: '',
    inflationRate: '',
    investmentReturn: '',
    yearsInRetirement: ''
  });

  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Initialize chart data
    const chartOptions = {
      series: [{
        name: 'Retirement Savings',
        data: [0]
      }],
      options: {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          line: {
            width: 3
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth'
        },
        xaxis: {
          categories: [],
          labels: {
            style: {
              fontSize: '12px',
              colors: '#666'
            }
          }
        },
        yaxis: {
          labels: {
            style: {
              fontSize: '12px',
              colors: '#666'
            },
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            }
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return `$${val.toFixed(2)}`;
            }
          }
        }
      }
    };
    setChartData(chartOptions);
  }, []);

  const calculateRetirement = () => {
    const { 
      currentAge, 
      retirementAge, 
      currentSavings, 
      monthlySavings, 
      expectedIncome, 
      inflationRate, 
      investmentReturn, 
      yearsInRetirement 
    } = formData;

    if (!currentAge || !retirementAge || !currentSavings || !monthlySavings || !expectedIncome || !inflationRate || !investmentReturn || !yearsInRetirement) {
      setError('Please fill in all required fields');
      return;
    }

    const age = parseInt(currentAge);
    const retireAge = parseInt(retirementAge);
    const savings = parseFloat(currentSavings);
    const monthly = parseFloat(monthlySavings);
    const income = parseFloat(expectedIncome);
    const inflation = parseFloat(inflationRate) / 100;
    const returnRate = parseFloat(investmentReturn) / 100 / 12;
    const years = parseInt(yearsInRetirement);

    if (isNaN(age) || isNaN(retireAge) || isNaN(savings) || isNaN(monthly) || isNaN(income) || isNaN(inflation) || isNaN(returnRate) || isNaN(years)) {
      setError('Please enter valid numbers for all fields');
      return;
    }

    if (age >= retireAge) {
      setError('Current age must be less than retirement age');
      return;
    }

    let yearsUntilRetirement = retireAge - age;
    let totalSavings = savings;
    let monthlyContributions = monthly;
    let retirementIncome = income;
    let savingsProgress = [];
    let yearsArray = [];

    // Calculate savings until retirement
    for (let year = 0; year < yearsUntilRetirement; year++) {
      // Monthly contributions with compound interest
      for (let month = 0; month < 12; month++) {
        totalSavings = totalSavings * (1 + returnRate) + monthlyContributions;
      }
      // Adjust for inflation
      monthlyContributions = monthlyContributions * (1 + inflation);
      retirementIncome = retirementIncome * (1 + inflation);
      savingsProgress.push(totalSavings);
      yearsArray.push(year + 1);
    }

    // Calculate retirement expenses
    let totalRetirementExpenses = 0;
    let adjustedIncome = retirementIncome;

    for (let year = 0; year < years; year++) {
      totalRetirementExpenses += adjustedIncome * 12;
      adjustedIncome = adjustedIncome * (1 + inflation);
    }

    // Calculate shortfall or surplus
    let retirementGap = totalRetirementExpenses - totalSavings;
    let monthlyAdjustment = Math.abs(retirementGap / (yearsUntilRetirement * 12));

    setResults({
      yearsUntilRetirement,
      totalSavings: `$${totalSavings.toFixed(2)}`,
      totalRetirementExpenses: `$${totalRetirementExpenses.toFixed(2)}`,
      retirementGap: retirementGap >= 0 ? `$${retirementGap.toFixed(2)}` : `-$${Math.abs(retirementGap).toFixed(2)}`,
      monthlyAdjustment: `$${monthlyAdjustment.toFixed(2)}`,
      monthlyContributions: `$${monthlyContributions.toFixed(2)}`,
      retirementIncome: `$${retirementIncome.toFixed(2)}`
    });

    // Update chart data
    if (chartData) {
      const updatedData = {
        ...chartData,
        series: [{
          name: 'Retirement Savings',
          data: savingsProgress
        }],
        options: {
          ...chartData.options,
          xaxis: {
            ...chartData.options.xaxis,
            categories: yearsArray.map(y => `Age ${age + y}`)
          }
        }
      };
      setChartData(updatedData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Retirement Calculator | Financial Tool</title>
        <meta name="description" content="Plan your retirement with our comprehensive Retirement Calculator. Calculate your retirement savings, expenses, and income needs." />
        <meta name="keywords" content="retirement calculator, financial planning, savings calculator, retirement planning" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="calculator-header mb-12">
          <div className="flex items-center gap-4">
            <div className="header-icon text-4xl">
              <FaRetweet />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Retirement Calculator</h1>
              <p className="text-lg text-blue-600 font-semibold">Plan Your Retirement Future</p>
            </div>
          </div>
        </div>

        <div className="calculator-description space-y-4 mb-12">
          <p className="text-gray-700">
            The Retirement Calculator helps you plan for your retirement by estimating your savings needs,
            potential income, and how much you need to save to achieve your retirement goals.
          </p>
        </div>

        <div className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="currentAge">
                Current Age
              </label>
              <input
                type="number"
                id="currentAge"
                name="currentAge"
                value={formData.currentAge}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="retirementAge">
                Retirement Age
              </label>
              <input
                type="number"
                id="retirementAge"
                name="retirementAge"
                value={formData.retirementAge}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="currentSavings">
                Current Savings
              </label>
              <input
                type="number"
                id="currentSavings"
                name="currentSavings"
                value={formData.currentSavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="monthlySavings">
                Monthly Savings
              </label>
              <input
                type="number"
                id="monthlySavings"
                name="monthlySavings"
                value={formData.monthlySavings}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="expectedIncome">
                Expected Retirement Income
              </label>
              <input
                type="number"
                id="expectedIncome"
                name="expectedIncome"
                value={formData.expectedIncome}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="$0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="inflationRate">
                Annual Inflation Rate (%)
              </label>
              <input
                type="number"
                id="inflationRate"
                name="inflationRate"
                value={formData.inflationRate}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="investmentReturn">
                Expected Investment Return (%)
              </label>
              <input
                type="number"
                id="investmentReturn"
                name="investmentReturn"
                value={formData.investmentReturn}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="yearsInRetirement">
                Expected Years in Retirement
              </label>
              <input
                type="number"
                id="yearsInRetirement"
                name="yearsInRetirement"
                value={formData.yearsInRetirement}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="0"
              />
            </div>

            <button
              onClick={calculateRetirement}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-md text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
            >
              Calculate Retirement
            </button>

            {error && (
              <div className="text-red-500 text-center mt-4">
                {error}
              </div>
            )}
          </div>

          {results && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Years Until Retirement</h3>
                  <p className="text-xl font-medium">{results.yearsUntilRetirement} years</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Retirement Savings</h3>
                  <p className="text-xl font-medium">{results.totalSavings}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Total Retirement Expenses</h3>
                  <p className="text-xl font-medium">{results.totalRetirementExpenses}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Retirement Gap</h3>
                  <p className="text-xl font-medium">{results.retirementGap}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Monthly Adjustment Needed</h3>
                  <p className="text-xl font-medium">{results.monthlyAdjustment}</p>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-md">
                  <h3 className="text-xl font-semibold mb-4">Retirement Income</h3>
                  <p className="text-xl font-medium">{results.retirementIncome}</p>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold mb-4">Retirement Savings Progress</h3>
                <div className="h-96">
                  {chartData && (
                    <Chart
                      options={chartData.options}
                      series={chartData.series}
                      type="line"
                      height={350}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            How to Use Retirement Calculator <FaInfoCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <ol className="list-decimal pl-8 space-y-4">
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your current age and expected retirement age</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Input your current savings and monthly contributions</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Enter your expected retirement income needs</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Add the expected inflation rate and investment return</span>
              </li>
              <li className="flex items-center">
                <FaCheckCircle className="text-green-600 mr-2" />
                <span>Click "Calculate Retirement" to see your results</span>
              </li>
            </ol>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Features and Benefits <FaChartLine className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Key Features</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Retirement savings projection</li>
                  <li>Inflation-adjusted calculations</li>
                  <li>Compound interest calculations</li>
                  <li>Retirement gap analysis</li>
                  <li>Monthly adjustment recommendations</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Benefits</h3>
                <ul className="list-disc pl-8 space-y-2">
                  <li>Plan for long-term financial security</li>
                  <li>Understand retirement income needs</li>
                  <li>Adjust savings to meet goals</li>
                  <li>Account for inflation and investment growth</li>
                  <li>Make informed retirement planning decisions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-24">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            Frequently Asked Questions <FaQuestionCircle className="ml-2 text-2xl" />
          </h2>
          <div className="bg-white p-8 rounded-xl shadow-md">
            <div className="space-y-8">
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is a retirement calculator?</h3>
                <p className="text-gray-700">
                  A retirement calculator helps you estimate how much you need to save for retirement and whether your current savings plan will meet your retirement goals.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does inflation affect my retirement?</h3>
                <p className="text-gray-700">
                  Inflation reduces the purchasing power of your money over time. The calculator adjusts your retirement expenses and income for inflation to give you a more accurate picture of your financial needs.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What if I have a retirement gap?</h3>
                <p className="text-gray-700">
                  If the calculator shows a retirement gap (where your expenses exceed your savings), it will suggest a monthly adjustment to help you close that gap. You can either increase your savings or adjust your retirement expectations.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Is this calculator accurate for my situation?</h3>
                <p className="text-gray-700">
                  This calculator provides an estimate based on the inputs you provide. Actual results may vary based on market conditions, personal circumstances, and other factors. It's recommended to consult with a financial advisor for personalized advice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;
